import React from 'react';
import { 
  ShieldAlert, 
  ShieldCheck, 
  AlertOctagon, 
  Lock, 
  Terminal, 
  ExternalLink, 
  CheckCircle,
  FileWarning,
  Eye
} from 'lucide-react';
import { ToolCall } from '../types';
import { formatTimeAgo } from '../services/timeAgo';

interface SecurityGuardrailsProps {
  calls: ToolCall[];
  onSelectCall: (call: ToolCall) => void;
}

export const SecurityGuardrails: React.FC<SecurityGuardrailsProps> = ({
  calls,
  onSelectCall
}) => {
  // Extract all intercepted 403 calls
  const blockedCalls = calls.filter(c => c.status === 403);

  const securityPolicies = [
    {
      id: 'SEC-ROOT-WIPE-01',
      name: 'Root Filesystem Destruction Guard',
      target: 'VPS & Mac',
      status: 'ACTIVE',
      desc: 'Intercepts recursive deletions on root, boot sectors, or essential system directories (rm -rf /, diskutil erase, etc.).'
    },
    {
      id: 'SEC-POL-PIPE-SUDO-001',
      name: 'Remote Subshell Pipe Elevation Prevention',
      target: 'VPS & Mac',
      status: 'ACTIVE',
      desc: 'Blocks unauthenticated piping of remote scripts directly into bash/zsh with sudo or root execution.'
    },
    {
      id: 'SEC-POL-CRED-EXFIL-004',
      name: 'Credential Store Containment',
      target: 'Mac & VPS',
      status: 'ACTIVE',
      desc: 'Restricts reading of ~/.ssh, /etc/shadow, macOS Keychain CLI dumps, or environment secret key stores.'
    },
    {
      id: 'SEC-POL-EGRESS-008',
      name: 'Egress Network Whitelist Firewall',
      target: 'Mesh Network',
      status: 'ACTIVE',
      desc: 'Ensures outbound network sockets only route via approved Tailscale mesh CIDRs and verified upstream APIs.'
    }
  ];

  return (
    <div className="space-y-6">
      
      {/* Security Status Header Card */}
      <div className="rounded-xl bg-gradient-to-r from-rose-950/40 via-slate-900 to-slate-900 border border-rose-800/50 p-5 shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-3 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-500/30 glow-rose">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white tracking-tight">
                  Security Guardrail Audit Stream
                </h2>
                <span className="px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 text-xs font-mono font-bold">
                  {blockedCalls.length} Interceptions
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl font-sans">
                Realtime interceptor preventing unauthorized privilege escalations, destructive terminal commands, 
                and credential containment violations before execution occurs on Mac agent or VPS gateway.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 font-mono text-xs">
            <div className="px-3 py-2 rounded-lg bg-slate-950/80 border border-slate-800">
              <span className="text-slate-400 block text-[10px]">Guardrail State</span>
              <span className="text-emerald-400 font-bold flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" />
                ENFORCING
              </span>
            </div>
            <div className="px-3 py-2 rounded-lg bg-slate-950/80 border border-slate-800">
              <span className="text-slate-400 block text-[10px]">Zero Breaches</span>
              <span className="text-cyan-300 font-bold">100% BLOCKED</span>
            </div>
          </div>
        </div>
      </div>

      {/* Active Security Policies Grid */}
      <div className="rounded-xl bg-slate-900/70 border border-slate-800 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-cyan-400" />
            <span>Active Automated Policy Filters (4 Enforced)</span>
          </h3>
          <span className="text-[11px] font-mono text-slate-400">Kernel & Proxy Layer</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {securityPolicies.map((pol) => (
            <div 
              key={pol.id}
              className="p-3.5 rounded-lg bg-slate-950/60 border border-slate-800/80 hover:border-slate-700 transition-colors space-y-1.5"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs font-semibold text-slate-200">{pol.name}</span>
                <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 text-[10px] font-mono border border-emerald-500/30">
                  {pol.status}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 font-sans leading-relaxed">
                {pol.desc}
              </p>
              <div className="flex items-center gap-3 pt-1 text-[10px] font-mono text-slate-400">
                <span>Rule: <code className="text-slate-300">{pol.id}</code></span>
                <span>•</span>
                <span>Scope: <span className="text-cyan-300">{pol.target}</span></span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Intercepted Calls List */}
      <div className="rounded-xl bg-slate-900/70 border border-slate-800 overflow-hidden shadow-xl space-y-3">
        <div className="p-4 border-b border-slate-800 bg-slate-950/80 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertOctagon className="w-4 h-4 text-rose-400" />
            <h3 className="text-sm font-bold text-white font-sans">
              Intercepted High-Risk Events (HTTP 403 Forbidden)
            </h3>
          </div>
          <span className="text-xs font-mono text-slate-400">
            {blockedCalls.length} events logged
          </span>
        </div>

        <div className="p-4 space-y-3">
          {blockedCalls.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-300">No Security Incidents Detected</p>
              <p className="text-xs text-slate-400 mt-1">All processed tool calls conform to strict security policy guardrails.</p>
            </div>
          ) : (
            blockedCalls.map((call) => (
              <div 
                key={call.id}
                onClick={() => onSelectCall(call)}
                className="group p-4 rounded-xl bg-slate-950/80 border border-rose-900/40 hover:border-rose-500/60 transition-all cursor-pointer space-y-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40 text-xs font-mono font-bold">
                      {call.securityAudit?.riskLevel || 'CRITICAL'} RISK
                    </span>
                    <span className="font-mono text-xs font-bold text-white">
                      {call.toolName} ({call.action})
                    </span>
                    <span className="text-xs font-mono text-slate-400">
                      ID: {call.id}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
                    <span>{new Date(call.timestamp).toLocaleTimeString()}</span>
                    <span className="px-1.5 py-0.5 rounded bg-slate-800 text-cyan-400 border border-slate-700 text-[10px] font-sans font-medium">
                      {formatTimeAgo(call.timestamp)}
                    </span>
                    <span className="text-slate-600">•</span>
                    <span className="text-cyan-400 font-semibold">{call.clientIp}</span>
                  </div>
                </div>

                {/* Reason & command */}
                <div className="text-xs text-rose-200/90 bg-rose-950/20 border border-rose-900/30 p-3 rounded-lg font-mono leading-relaxed">
                  <div className="text-rose-400 font-semibold mb-1 flex items-center gap-1.5">
                    <FileWarning className="w-3.5 h-3.5" />
                    <span>Violation: {call.securityAudit?.violationReason || call.errorMessage}</span>
                  </div>
                  {call.securityAudit?.blockedPattern && (
                    <div className="text-slate-300 text-[11px] mt-1">
                      Matched Pattern: <code className="bg-rose-900/50 px-1.5 py-0.5 rounded text-rose-200">{call.securityAudit.blockedPattern}</code>
                    </div>
                  )}
                </div>

                {/* Parameter snippet preview */}
                <div className="flex items-center justify-between pt-1 text-xs font-mono">
                  <div className="text-slate-400 truncate max-w-xl">
                    <span className="text-slate-400">Payload: </span>
                    <code className="text-slate-300 text-[11px]">
                      {JSON.stringify(call.parameters).substring(0, 100)}...
                    </code>
                  </div>
                  <button 
                    className="flex items-center gap-1 text-cyan-400 group-hover:text-cyan-300 text-xs font-medium shrink-0"
                  >
                    <span>Inspect</span>
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  );
};
